import { HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { map } from 'rxjs/operators';
import { ConstructAPI } from 'src/app/API/constructAPI';
import { RestApiService } from 'src/app/API/restapi';
import { Customer, IAPICustomer } from 'src/model/customer.model';
import { FileInterface } from 'src/model/file.interface';
import { PersonAdapter } from 'src/model/person.model';
import { Profile } from 'src/model/profile.model';
import { FileAdapter, FileUrlBuilder } from './file.service';
import { ProfileAdapter } from './profile.service';

export interface CustomerCount {
  CompanyCode: string,
  CompanyId: number,
  Count: number
}

interface CustCountOptions {
  verified?: boolean,
  status?: string,
  priority?: number,
  minPriority?: number,
  maxPriority?: number
}

@Injectable({
  providedIn: 'root'
})
export class CustomerAdapter extends PersonAdapter {

  adapt(item: any, arrProfiles?: Profile[]): Customer {
    return new Customer(
      item.customerId ?? item.id,
      item.name,
      item.occupation,
      item.salary,
      item.salaryDay,
      item.icNumber,
      item.contactNo,
      item.remark,
      item.ctosNumber,
      item.ctosStatus,
      item.status && this.processCustomerStatus(item.status),
      new Date(item.dateCreated),
      item.handler,
      item.companyId,
      item.customerCode,
      item.companyCode,
      item.reserve ?? 0,
      item.badDebt,
      item.address,
      arrProfiles ?? (item.profileIds && item.profileIds.map(p => new Profile(p))),
      (item.lastFormJDate && new Date(item.lastFormJDate)),
      item.needAttention,
      (item.epfDate && new Date(item.epfDate)), // if (item.epfDate)
      item.address2,
      item.verifiedOn ? true : false,
      new Date(item.dob),
      item.verifiedBy,
      item.otherId,
      item.race,
      item.gender,
      (item.verifiedOn && new Date(item.verifiedOn))
    )
  }

  toApiModel(cust: Customer): IAPICustomer {
    return {
      ...(cust.ID && { id: cust.ID }),
      ...(cust.Name && { name: cust.Name }),
      occupation: cust.Occupation,
      salary: cust.Salary,
      salaryDay: cust.SalaryDay,
      icNumber: cust.ICNumber,
      contactNo: cust.ContactNo,
      ...(cust.Remark && { remark: cust.Remark }),
      ...(cust.CtosNumber && { ctosNumber: cust.CtosNumber }),
      ...(cust.CtosStatus && { ctosStatus: cust.CtosStatus }),
      ...(cust.Status && cust.Status.length > 0 && { status: cust.Status.map(s => s.StatusCode) }),
      ...(cust.DateCreated && { dateCreated: cust.DateCreated.toISOString() }),
      handler: cust.HandlerId,
      companyId: cust.CompanyId,
      customerCode: cust.CustomerCode,
      ...(cust.CompanyCode && { companyCode: cust.CompanyCode }),
      ...(cust.Reserve && { reserve: cust.Reserve }),
      ...(cust.BadDebt && { badDebt: cust.BadDebt }),
      address: cust.Address,
      ...(cust.Profiles && { profileIds: cust.Profiles.map(p => p.ID) }),
      ...(cust.LastFormJDate && { lastFormJDate: this.transformDate(cust.LastFormJDate) }),
      ...(cust.NeedAttention && { needAttention: cust.NeedAttention }),
      ...(cust.EPFDate && { epfDate: this.transformDate(cust.EPFDate) }),
      address2: cust.Address2,
      // verified: cust.Verified,
      ...(cust.DOB && { dob: this.transformDate(cust.DOB) }),
      // ...(cust.VerifiedBy && { verifiedBy: cust.VerifiedBy }),
      ...(cust.OtherID && { otherId: cust.OtherID }),
      race: cust.Race,
      gender: cust.Gender,
      // verifiedOn: cust.VerifiedOn.toISOString()
    };
  }

  adaptCustomerCount(item: any): CustomerCount {
    const result: CustomerCount = {
      CompanyCode: item.companyCode,
      CompanyId: item.companyId,
      Count: item.count
    }
    return result;
  }
}

@Injectable({
  providedIn: 'root'
})
export class CustomerService {
  private api = inject(RestApiService);
  private constructApi = inject(ConstructAPI);
  private adapter = inject(CustomerAdapter);
  private profileAdapter = inject(ProfileAdapter);
  private fileAdapter = inject(FileAdapter);
  private fileUrl = inject(FileUrlBuilder);

  getAllCustomers(): Promise<Customer[]> {
    return new Promise<Customer[]>((resolve, reject) => {
      this.api.get<Customer[]>(this.constructApi.getAllCustomers()).pipe(
        map(data => data.map(d => this.adapter.adapt(d)))
      ).subscribe({
        next: (response) => { console.log('res: ', response); resolve(response) },
        error: err => {
          this.api.handleError(err);
          console.error('Failed to get all customers: ', err);
          reject(err);
        }
      })
    })
  }

  getCompanyLeads(compID: number): Promise<Customer[]> {
    return new Promise<Customer[]>((resolve, reject) => {
      this.api.get<Customer[]>(this.constructApi.getCompanyLeads(compID)).pipe(
        map(data => data.map(d => this.adapter.adapt(d)))
      ).subscribe({
        next: response => resolve(response),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to get all leads of company: ', err);
          reject(err);
        }
      })
    })
  }

  getCompanyAllCustomers(compID: number, verified?: boolean): Promise<Customer[]> {
    return new Promise<Customer[]>((resolve, reject) => {
      this.api.get<Customer[]>(this.constructApi.getCompanyAllCustomers(compID, verified)).pipe(
        map(data => data.map(d => this.adapter.adapt(d)))
      ).subscribe({
        next: response => resolve(response),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to get all customers of company: ', err);
          reject(err);
        }
      })
    })
  }

  getCustomerDetails(custID: number): Promise<Customer> {
    return new Promise<Customer>((resolve, reject) => {
      this.api.get<Customer>(this.constructApi.getCustomerDetails(custID)).pipe(
        map(data => this.adapter.adapt(data))
      ).subscribe({
        next: response => {
          resolve(response);
        },
        error: err => {
          this.api.handleError(err);
          console.error('Failed to get customer details: ', err);
          reject(err);
        }
      })
    });
  }

  getCheckCustomerExisted(ic: string, code: string): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.api.head(this.constructApi.getCheckCustomerExistence(ic, code)).pipe(
        map((res: HttpResponse<any>) => res.status === 200)
      ).subscribe({
        next: response => {
          resolve(response);
        },
        error: err => {
          if (err.status === 404) {
            return false;
          }
          // reject(this.api.handleError(err));
          console.error('Failed to check customer existence: ', err);
          reject(err)
        }
      })
    });
  }

  postCustomer(cust: Customer, isCP?: boolean): Promise<Customer> {
    return new Promise<Customer>((resolve, reject) => {
      const address = (isCP) ? this.constructApi.getAddCPSettledCustomer() : this.constructApi.getAddCustomer();
      this.api.post<Customer>(address, this.adapter.toApiModel(cust)).subscribe({
        next: res => {
          resolve(this.adapter.adapt(res));
        },
        error: err => {
          if (err.status !== 409) this.api.handleError(err);
          console.error('Failed to add customer: ', err);
          reject(err);
        }
      });
    })
  }

  updateCustomerDOB(customerId: number, dob: Date): Promise<Customer> {
    const data = { dob: this.adapter.transformDate(dob) };
    return new Promise<Customer>((resolve, reject) => {
      this.api.put<Customer>(this.constructApi.getUpdateDOB(customerId), data).subscribe({
        next: res => resolve(this.adapter.adapt(res)),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to update customer dob: ', err);
          reject(err);
        }
      });
    })
  }

  putCustomer(custID: number, cust: Customer): Promise<Customer> {
    return new Promise<Customer>((resolve, reject) => {
      this.api.put<Customer>(this.constructApi.getUpdateCustomer(custID), this.adapter.toApiModel(cust)).subscribe({
        next: res => resolve(this.adapter.adapt(res)),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to update customer: ', err);
          reject(err);
        }
      });
    })
  }

  getCustomerTypedFileExistence(custID: number, fileType: string) {
    return new Promise<boolean>((resolve, reject) => {
      this.api.get<boolean>(this.constructApi.getCustomerTypedFileExistence(custID, fileType)).subscribe({
        next: response => {
          resolve(Boolean(response));
        },
        error: err => {
          this.api.handleError(err);
          console.error(err);
          reject(err);
        }
      })
    })
  }

  getCustomerAllFiles(customerId: number): Promise<FileInterface[]> {
    return new Promise<FileInterface[]>((resolve, reject) => {
      this.api.get<FileInterface[]>(this.constructApi.getAllFiles(customerId)).pipe(
        map(data => data.map(d => this.fileAdapter.adapt(d, this.fileUrl.buildCustomerFileUrl(customerId))))
      ).subscribe({
        next: response => resolve(response),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to get customer files: ', err);
          reject(err);
        }
      })
    })
  }

  // #region 'Customer with Profile info (Without reserve, bad debt)'
  getAllLeadsProfiles(): Promise<Customer[]> {
    return new Promise<Customer[]>((resolve, reject) => {
      this.api.get<Customer[]>(this.constructApi.getAllLeadsProfiles()).pipe(
        map(data => data.map((d) => {
          const p = this.profileAdapter.adapt(d);
          return this.adapter.adapt(d, [p]);
        }))
      ).subscribe({
        next: response => resolve(response),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to get all leads: ', err);
          reject(err);
        }
      })
    })
  }

  getCompanyLeadsProfiles(compID: number): Promise<Customer[]> {
    return new Promise<Customer[]>((resolve, reject) => {
      this.api.get<Profile[]>(this.constructApi.getCompanyLeadsProfiles(compID)).pipe(
        map(data => data.map((d) => {
          const p = this.profileAdapter.adapt(d);
          return this.adapter.adapt(d, [p]);
        }))
      ).subscribe({
        next: response => resolve(response),
        error: err => {
          this.api.handleError(err)
          console.error('Failed to get all leads of company: ', err);
          reject(err);
        }
      })
    })
  }
  // #endregion

  // #region 'Customer count'
  getCustomerCount(o?: CustCountOptions): Promise<CustomerCount[]> {
    return new Promise<CustomerCount[]>((resolve, reject) => {
      this.api.get<CustomerCount[]>(this.constructApi.getCustomerCount(o.verified, o.status, o.priority, o.minPriority, o.maxPriority)).pipe(
        map(data => data.map(d => this.adapter.adaptCustomerCount(d)))
      ).subscribe({
        next: response => resolve(response),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to get customer count: ', err);
          reject(err);
        }
      })
    })
  }

  getCompCustomerCount(compId: number, o?: CustCountOptions): Promise<CustomerCount> {
    // TODO: response length should be exactly 1
    return new Promise<CustomerCount>((resolve, reject) => {
      this.api.get<CustomerCount[]>(this.constructApi.getCompCustomerCount(compId, o.verified, o.status, o.priority, o.minPriority, o.maxPriority)).pipe(
        map(data => data.map(d => this.adapter.adaptCustomerCount(d)))
      ).subscribe({
        next: response => resolve(response[0]),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to get company customer count: ', err);
          reject(err);
        }
      })
    })
  }
  // #endregion
}